/**
 * V38.22 — CAP BATI+ CMI : panneau de gestion des devis sauvegardés.
 *
 * Fonctionnalités :
 *   - Liste paginée des devis (avec filtre archivés on/off)
 *   - Sauvegarder un nouveau devis à partir du formulaire courant
 *   - Sélection multiple (checkboxes) + actions groupées :
 *       • Archiver / Restaurer / Supprimer (DRAFT only)
 *       • Exporter en Excel (.xlsx) — sélection ou tout
 *   - Actions unitaires :
 *       • Télécharger PDF DRAFT
 *       • Envoyer par email au constructeur (Brevo, PJ DRAFT)
 *
 * Utilisé dans : SimulateurCapBatiPlusCmi.jsx (sous la carte résultat).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Archive, ArchiveRestore, Trash2, FileDown, Mail, FileSpreadsheet,
  RefreshCw, Loader2, Eye, EyeOff, Copy,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ccmi-cap-bati/cap-bati-plus-cmi`;

const fmtEUR = (n) => Number(n || 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
});

const statusColor = (s) => ({
  draft: 'bg-slate-200 text-slate-700',
  signed: 'bg-emerald-100 text-emerald-800',
  invoiced: 'bg-blue-100 text-blue-800',
  paid: 'bg-purple-100 text-purple-800',
}[s] || 'bg-slate-100 text-slate-600');

export default function CapBatiQuotesPanel({
  buildPayload,      // () => payload pour POST /quotes (depuis le form parent)
  projectId = null,
  projectRef = null,
  defaultConstructorEmail = '',
  onLoadIntoForm = null,  // V38.23 — callback(quote) pour duplication / prefill form
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [emailDialog, setEmailDialog] = useState({ open: false, quote: null });
  const [emailForm, setEmailForm] = useState({
    to_email: '', to_name: '', subject: '', message: '', cc_emails: '',
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchQuotes = useCallback(async () => {
    try {
      const params = {
        limit: 100,
        is_archived: showArchived ? true : false,
      };
      if (projectId) params.project_id = projectId;
      const r = await axios.get(`${API}/quotes`, { params });
      return { items: r.data.items || [], total: r.data.total || 0, ok: true };
    } catch {
      return { items: [], total: 0, ok: false };
    }
  }, [projectId, showArchived]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await fetchQuotes();
    if (!r.ok) toast.error('Erreur chargement devis');
    setItems(r.items);
    setTotal(r.total);
    setLoading(false);
  }, [fetchQuotes]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchQuotes();
      if (cancelled) return;
      if (!r.ok) toast.error('Erreur chargement devis');
      setItems(r.items);
      setTotal(r.total);
    })();
    return () => { cancelled = true; };
  }, [fetchQuotes]);

  const handleSave = useCallback(async () => {
    if (!buildPayload) return;
    const payload = buildPayload();
    if (!payload) {
      toast.error('Formulaire invalide');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/quotes`, {
        ...payload,
        project_id: projectId || undefined,
        project_reference: projectRef || undefined,
        constructor_email: defaultConstructorEmail || undefined,
      });
      toast.success(`Devis ${r.data.quote.reference} enregistré`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec enregistrement');
    } finally {
      setSaving(false);
    }
  }, [buildPayload, projectId, projectRef, defaultConstructorEmail, refresh]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((it) => it.id));
    });
  };

  const runBulkAction = async (action) => {
    if (selectedIds.size === 0) {
      toast.error('Aucun devis sélectionné');
      return;
    }
    const confirmMsg = {
      archive: `Archiver ${selectedIds.size} devis ?`,
      restore: `Restaurer ${selectedIds.size} devis ?`,
      delete: `Supprimer définitivement ${selectedIds.size} devis DRAFT ?\n(Action irréversible)`,
    }[action];
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await axios.post(`${API}/quotes/bulk`, {
        quote_ids: Array.from(selectedIds),
        action,
      });
      const count = r.data.modified ?? r.data.deleted ?? 0;
      toast.success(`${count} devis ${action === 'delete' ? 'supprimés' : action === 'archive' ? 'archivés' : 'restaurés'}`);
      setSelectedIds(new Set());
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec action groupée');
    }
  };

  const downloadDraftPdf = async (quote) => {
    try {
      const r = await axios.get(`${API}/quotes/${quote.id}/draft-pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_cap_bati_draft_${quote.reference}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Échec téléchargement DRAFT');
    }
  };

  const exportExcel = async (onlySelected = false) => {
    try {
      const params = { is_archived: showArchived ? true : false };
      if (projectId) params.project_id = projectId;
      if (onlySelected && selectedIds.size > 0) {
        params.quote_ids = Array.from(selectedIds).join(',');
      }
      const r = await axios.get(`${API}/quotes/export.xlsx`, {
        params, responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_cap_bati_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch {
      toast.error('Échec export Excel');
    }
  };

  const openEmailDialog = (quote) => {
    setEmailForm({
      to_email: quote.constructor_email || defaultConstructorEmail || '',
      to_name: '',
      subject: `Devis CAP BATI+ CMI ${quote.reference} (DRAFT)`,
      message: '',
      cc_emails: '',
    });
    setEmailDialog({ open: true, quote });
  };

  const sendEmail = async () => {
    const quote = emailDialog.quote;
    if (!quote || !emailForm.to_email) {
      toast.error('Adresse destinataire requise');
      return;
    }
    setSendingEmail(true);
    try {
      const cc = (emailForm.cc_emails || '')
        .split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
      await axios.post(`${API}/quotes/${quote.id}/send-email`, {
        to_email: emailForm.to_email,
        to_name: emailForm.to_name || undefined,
        subject: emailForm.subject || undefined,
        message: emailForm.message || undefined,
        cc_emails: cc.length > 0 ? cc : undefined,
      });
      toast.success(`Devis envoyé à ${emailForm.to_email}`);
      setEmailDialog({ open: false, quote: null });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec envoi email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Card className="border-blue-200" data-testid="cap-bati-quotes-panel">
      <CardHeader className="bg-blue-50 rounded-t-lg">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileDown className="h-5 w-5 text-blue-700" />
              Devis CAP BATI+ CMI sauvegardés ({total})
            </CardTitle>
            <CardDescription>
              Persistance, sélection groupée, export Excel, envoi constructeur.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline" onClick={refresh} disabled={loading}
              data-testid="quotes-refresh-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button
              size="sm" variant="outline" onClick={() => setShowArchived((v) => !v)}
              data-testid="quotes-toggle-archived"
            >
              {showArchived
                ? <><Eye className="h-4 w-4 mr-1" /> Voir actifs</>
                : <><EyeOff className="h-4 w-4 mr-1" /> Voir archivés</>}
            </Button>
            <Button
              size="sm" className="bg-blue-700 hover:bg-blue-800 text-white"
              onClick={handleSave} disabled={saving || !buildPayload}
              data-testid="quotes-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Sauvegarder ce devis
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        {/* Barre d'actions groupées */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="quotes-bulk-actions">
          <Badge variant="secondary" className="font-mono">
            {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
          </Badge>
          {!showArchived && (
            <Button
              size="sm" variant="outline" disabled={selectedIds.size === 0}
              onClick={() => runBulkAction('archive')}
              data-testid="bulk-archive-btn"
            >
              <Archive className="h-4 w-4 mr-1" /> Archiver
            </Button>
          )}
          {showArchived && (
            <Button
              size="sm" variant="outline" disabled={selectedIds.size === 0}
              onClick={() => runBulkAction('restore')}
              data-testid="bulk-restore-btn"
            >
              <ArchiveRestore className="h-4 w-4 mr-1" /> Restaurer
            </Button>
          )}
          <Button
            size="sm" variant="outline" disabled={selectedIds.size === 0}
            onClick={() => runBulkAction('delete')}
            className="text-red-700 border-red-300 hover:bg-red-50"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Supprimer (DRAFT)
          </Button>
          <div className="border-l h-6 mx-1" />
          <Button
            size="sm" variant="outline"
            onClick={() => exportExcel(false)}
            data-testid="export-all-xlsx-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel (tout)
          </Button>
          <Button
            size="sm" variant="outline"
            disabled={selectedIds.size === 0}
            onClick={() => exportExcel(true)}
            data-testid="export-selected-xlsx-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel (sélection)
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-8 text-slate-400" data-testid="quotes-loading">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm" data-testid="quotes-empty">
            Aucun devis {showArchived ? 'archivé' : 'enregistré'}.
          </div>
        ) : (
          <div className="overflow-x-auto border rounded">
            <table className="w-full text-xs" data-testid="quotes-table">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-2 py-2 text-left w-8">
                    <Checkbox
                      checked={selectedIds.size === items.length && items.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-2 py-2 text-left">Référence</th>
                  <th className="px-2 py-2 text-left">Projet</th>
                  <th className="px-2 py-2 text-left">Constructeur</th>
                  <th className="px-2 py-2 text-right">Total TTC</th>
                  <th className="px-2 py-2 text-left">Statut</th>
                  <th className="px-2 py-2 text-left">Créé le</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr
                    key={q.id}
                    className="border-t hover:bg-slate-50"
                    data-testid={`quote-row-${q.id}`}
                  >
                    <td className="px-2 py-2">
                      <Checkbox
                        checked={selectedIds.has(q.id)}
                        onCheckedChange={() => toggleSelect(q.id)}
                        data-testid={`quote-checkbox-${q.id}`}
                      />
                    </td>
                    <td className="px-2 py-2 font-mono">{q.reference}</td>
                    <td className="px-2 py-2">{q.project_reference || '—'}</td>
                    <td className="px-2 py-2 truncate max-w-[180px]">{q.constructor_email || '—'}</td>
                    <td className="px-2 py-2 text-right font-semibold">{fmtEUR(q.amount_total_ttc)}</td>
                    <td className="px-2 py-2">
                      <Badge className={statusColor(q.status)}>{q.status}</Badge>
                      {q.is_archived && <Badge className="ml-1 bg-amber-100 text-amber-800 font-semibold">archivé</Badge>}
                    </td>
                    <td className="px-2 py-2 text-slate-500">
                      {q.created_at ? new Date(q.created_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {onLoadIntoForm && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => onLoadIntoForm(q)}
                          title="Dupliquer : recharger ce devis dans le formulaire pour ajustement"
                          data-testid={`quote-duplicate-${q.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => downloadDraftPdf(q)}
                        title="Télécharger PDF DRAFT"
                        data-testid={`quote-draft-pdf-${q.id}`}
                      >
                        <FileDown className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => openEmailDialog(q)}
                        title="Envoyer au constructeur"
                        data-testid={`quote-email-${q.id}`}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Dialog envoi email */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => !o && setEmailDialog({ open: false, quote: null })}>
        <DialogContent className="max-w-lg" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer le devis au constructeur</DialogTitle>
            <DialogDescription>
              Référence : <span className="font-mono">{emailDialog.quote?.reference}</span> ·
              Total : <strong>{fmtEUR(emailDialog.quote?.amount_total_ttc)}</strong>
              <br/>Le PDF DRAFT sera joint en pièce jointe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="to_email">Destinataire (email)</Label>
              <Input
                id="to_email" type="email"
                value={emailForm.to_email}
                onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                data-testid="email-to-input"
              />
            </div>
            <div>
              <Label htmlFor="to_name">Nom du destinataire (optionnel)</Label>
              <Input
                id="to_name"
                value={emailForm.to_name}
                onChange={(e) => setEmailForm({ ...emailForm, to_name: e.target.value })}
                data-testid="email-name-input"
              />
            </div>
            <div>
              <Label htmlFor="subject">Sujet</Label>
              <Input
                id="subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                data-testid="email-subject-input"
              />
            </div>
            <div>
              <Label htmlFor="cc_emails">CC (emails séparés par virgule)</Label>
              <Input
                id="cc_emails"
                placeholder="cooper@…, expert@…"
                value={emailForm.cc_emails}
                onChange={(e) => setEmailForm({ ...emailForm, cc_emails: e.target.value })}
                data-testid="email-cc-input"
              />
            </div>
            <div>
              <Label htmlFor="message">Message personnalisé (optionnel)</Label>
              <Textarea
                id="message" rows={4}
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                data-testid="email-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline" onClick={() => setEmailDialog({ open: false, quote: null })}
              data-testid="email-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={sendEmail} disabled={sendingEmail || !emailForm.to_email}
              className="bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="email-send-btn"
            >
              {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
